import { useQuery } from "@apollo/client";
import Construction from "@mui/icons-material/Construction";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Search from "@mui/icons-material/Search";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { lighten } from "@mui/material/styles";
import { useAlerts } from "components/Alerts/AlertProvider";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useDebounce } from "hooks/useDebounce";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import type { FlagRow } from "./FlagsTab";

import EncampLogo from "../../../assets/EncampLogo.svg";
import { gql } from "../../../generated-graphql";
import { ModuleSubscriptionStatus } from "../../../generated-graphql/graphql";
import { formatPercent } from "./flagAdoption";
import { FlagAnnotationEditor } from "./FlagAnnotationEditor";

/**
 * Pulls the paginated list of tenants that have a TenantConfig override
 * for this flag with `value=true`. The `hasFlag:<key>` omnisearch token
 * is the existing server-side filter shared with the Staff Organizations
 * grid, so we re-use it here rather than introducing a flag-specific
 * resolver. Pagination is page-size based (rather than cursor-based) so
 * the "Load more" button can simply bump the size and re-fetch — Apollo
 * caches by `(search, pageSize)` so each step is one network round-trip.
 */
const FLAG_TENANTS_QUERY = gql(`
  query StaffFlagTenants($search: String!, $pageSize: Int!) {
    tenants(search: $search, pageSize: $pageSize, sort: [{ field: "name", sort: "asc" }]) {
      items {
        id
        name
        isDemo
        isDisabled
      }
      count
    }
  }
`);

const TENANTS_LIST_INITIAL_PAGE_SIZE = 100;
const TENANTS_LIST_LOAD_MORE_INCREMENT = 100;

/**
 * Lazy query fired only when the drawer opens on a flag that has a
 * subscription mapping. Returns aggregate counts + the first page of
 * each discrepancy bucket. Server returns `null` when the flag has
 * no mapping (the drawer skips the entire subscription block in that
 * case).
 */
const FLAG_SUBSCRIPTION_OVERVIEW_QUERY = gql(`
  query StaffFlagSubscriptionOverview($flagKey: String!, $pageSize: Int!) {
    flagSubscriptionOverview(flagKey: $flagKey) {
      flagKey
      subscribedCount
      flagOnSubOffCount
      flagOffSubOnCount
      flagOnSubOffTenants(page: 0, pageSize: $pageSize) {
        items {
          tenantId
          tenantName
          isPartner
          subscriptionStatus
        }
        count
      }
      flagOffSubOnTenants(page: 0, pageSize: $pageSize) {
        items {
          tenantId
          tenantName
          isPartner
          subscriptionStatus
        }
        count
      }
    }
  }
`);

const SUBSCRIPTION_DISCREPANCY_PAGE_SIZE = 50;

function PropertyStrip({ flag }: { flag: FlagRow }) {
  const typeLabel = flag.type === "MODULE" ? "Module" : flag.type === "ADD_ON" ? "Add-on" : "Other";
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip label={typeLabel} size="small" />
      <Tooltip
        title={`This flag defaults to ${
          flag.defaultValue ? "ON" : "OFF"
        } for all tenants unless explicitly set`}
      >
        {/* Default-ON gets the same pale success-green treatment as
            the equivalent ON chip in the cross-tenant FlagsTab grid
            so the two surfaces stay in sync. OFF keeps the neutral
            default. */}
        <Chip
          label={flag.defaultValue ? "Default: ON" : "Default: OFF"}
          size="small"
          sx={
            flag.defaultValue
              ? (theme) => ({
                  bgcolor: lighten(theme.palette.success.light, 0.7),
                  color: theme.palette.success.dark,
                })
              : undefined
          }
        />
      </Tooltip>
      {flag.staffOverride && (
        <Tooltip title="Staff users see this feature regardless of the flag value">
          <Chip
            size="small"
            // EncampLogo as an avatar to match the toggle panel's
            // icon-only affordance for staff-override flags. 12px keeps
            // it visually balanced with the chip's text height; the
            // earlier 16px overpowered the label.
            avatar={
              <Box
                component="img"
                src={EncampLogo}
                alt=""
                sx={{ width: 12, height: 12, ml: 0.5 }}
              />
            }
            label="Staff-visible"
          />
        </Tooltip>
      )}
      {flag.inDevelopment && (
        <Tooltip title="This feature is currently being developed">
          {/* Construction icon as a Chip avatar — same icon the
              grid's InDevelopmentCell renders, so the two surfaces
              share an at-a-glance affordance for "still being
              built". Transparent Avatar lets the icon sit on the
              chip's own fill without a competing circular
              background. The default `.MuiChip-avatarSmall` font
              size is 0.625rem (~10px) which makes a `fontSize:
              "inherit"` icon almost invisible, so we set an
              explicit 16px to fill the 18px avatar. */}
          <Chip
            label="In development"
            size="small"
            avatar={
              <Avatar sx={{ bgcolor: "transparent" }}>
                <Construction sx={{ fontSize: 16, color: "text.secondary" }} />
              </Avatar>
            }
          />
        </Tooltip>
      )}
    </Stack>
  );
}

function DependencySection({
  title,
  keys,
  flagsByKey,
  onSelect,
}: {
  title: string;
  keys: string[];
  flagsByKey: Map<string, FlagRow>;
  onSelect: (flag: FlagRow) => void;
}) {
  if (keys.length === 0) return null;
  return (
    <Stack spacing={0.5}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {keys.map((key) => {
          const dep = flagsByKey.get(key);
          if (!dep) {
            // Unknown key — render as a plain monospace chip so staff
            // see something is referenced even if metadata is stale.
            return <Chip key={key} label={key} size="small" />;
          }
          return (
            <Chip
              key={key}
              label={dep.label}
              size="small"
              clickable
              onClick={() => onSelect(dep)}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}

/**
 * One adoption tile (Enabled or Disabled). Shows the effective total
 * (`count`) prominently and an optional `breakdown` caption beneath it
 * that splits explicit overrides from default-inherited tenants. Only
 * the side that matches the flag's `defaultValue` carries a breakdown —
 * the opposite side is always all-explicit by construction.
 */
function StatTile({
  label,
  count,
  denominator,
  breakdown,
  helpText,
}: {
  label: string;
  count: number;
  denominator: number;
  breakdown?: { explicit: number; fromDefault: number };
  helpText: string;
}) {
  return (
    <Tooltip title={helpText} placement="top" arrow>
      <Stack
        spacing={0.25}
        sx={{
          flex: 1,
          minWidth: 0,
          p: 1.5,
          borderRadius: 1,
          bgcolor: "background.default",
          border: 1,
          borderColor: "divider",
          cursor: "help",
        }}
      >
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {count}
          </Typography>
          {denominator > 0 && (
            <Typography variant="caption" color="text.secondary">
              ({formatPercent(count, denominator)})
            </Typography>
          )}
        </Stack>
        {breakdown ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {breakdown.explicit} explicit · {breakdown.fromDefault} from default
          </Typography>
        ) : (
          // Empty caption preserves the tile height so the two boxes
          // stay vertically aligned.
          <Typography variant="caption" color="text.secondary">
            &nbsp;
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}

function AdoptionStats({
  flag,
  customerTenantCount,
  subscribedCount,
  subscriptionLabel,
}: {
  flag: FlagRow;
  customerTenantCount: number;
  /**
   * Number of active customer tenants whose mapped subscription
   * column equals `CUSTOMER`. Undefined when the flag has no
   * subscription mapping or the lazy query is still loading — in
   * either case we just don't render the third tile.
   */
  subscribedCount?: number;
  subscriptionLabel?: string;
}) {
  // Tenants without a TenantConfig row inherit `defaultValue`. We only
  // include the customer-tenant denominator (active, non-demo) — see
  // useCustomerTenantCount.
  const noRowCount = Math.max(0, customerTenantCount - flag.tenantOnCount - flag.tenantOffCount);

  // Effective totals fold the no-row group into whichever side matches
  // the flag's default. By construction the *other* side is therefore
  // always all-explicit.
  const enabledTotal = flag.tenantOnCount + (flag.defaultValue ? noRowCount : 0);
  const disabledTotal = flag.tenantOffCount + (flag.defaultValue ? 0 : noRowCount);

  const enabledBreakdown = flag.defaultValue
    ? { explicit: flag.tenantOnCount, fromDefault: noRowCount }
    : undefined;
  const disabledBreakdown = flag.defaultValue
    ? undefined
    : { explicit: flag.tenantOffCount, fromDefault: noRowCount };

  return (
    <Stack spacing={1}>
      <Typography variant="overline" color="text.secondary">
        Tenant Adoption
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <StatTile
          label="Enabled"
          count={enabledTotal}
          denominator={customerTenantCount}
          breakdown={enabledBreakdown}
          helpText={
            flag.defaultValue
              ? "Count of active customer tenants where this flag is either explicitly enabled or not set (this flag defaults to ON)"
              : "Count of active customer tenants that have this flag explicitly enabled"
          }
        />
        <StatTile
          label="Disabled"
          count={disabledTotal}
          denominator={customerTenantCount}
          breakdown={disabledBreakdown}
          helpText={
            flag.defaultValue
              ? "Count of active customer tenants that have this flag explicitly disabled"
              : "Count of active customer tenants where this flag is either explicitly disabled or not set (this flag defaults to OFF)"
          }
        />
        {subscribedCount !== undefined && (
          <StatTile
            label="Subscribed"
            count={subscribedCount}
            denominator={customerTenantCount}
            helpText={
              subscriptionLabel
                ? `Count of active customer tenants whose Salesforce ${subscriptionLabel} subscription is CUSTOMER`
                : "Count of active customer tenants whose Salesforce subscription for this module is CUSTOMER"
            }
          />
        )}
      </Stack>
    </Stack>
  );
}

// `subscriptionStatus` reuses the codegen-generated GraphQL enum so
// that adding a new `ModuleSubscriptionStatus` variant on the server
// becomes a compile-time signal here instead of an unrecognized
// string at runtime. Nullable because the GraphQL field is nullable
// — tenants with no `TenantSubscription` row report null. Treating
// null and undefined the same at the view layer (both render as "—")
// avoids forcing a normalize pass on the Apollo result.
type DiscrepancyTenantRow = {
  tenantId: string;
  tenantName: string;
  isPartner: boolean;
  subscriptionStatus?: ModuleSubscriptionStatus | null;
};

/**
 * One row in the unified discrepancy list. Each entry carries the
 * direction so a single sorted list can render both
 * flag-on/sub-off and flag-off/sub-on cases interleaved.
 */
type DiscrepancyDirection = "flagOnSubOff" | "flagOffSubOn";
type UnifiedDiscrepancyRow = DiscrepancyTenantRow & {
  direction: DiscrepancyDirection;
};

/**
 * Top-level Subscriptions / Discrepancies section. Renders a single
 * unified, alphabetically sorted list with a direction Chip per row
 * (color-coded so both sides are scannable at a glance). The
 * Subscribed StatTile in `AdoptionStats` already conveys that the
 * check ran for this flag, so when both buckets are empty (and
 * there's no error) we suppress the whole section heading — empty
 * "every tenant aligned" copy would be noise.
 *
 * The Chip tooltips reuse the helper sentences the old paired sub-
 * lists displayed as captions so staff still get the FORMER_CUSTOMER
 * vs unset / paying-but-off context when they hover.
 */
function SubscriptionDiscrepancies({
  flag,
  overview,
  loading,
  error,
}: {
  flag: FlagRow;
  overview: {
    flagOnSubOffCount: number;
    flagOffSubOnCount: number;
    flagOnSubOffTenants: { items: DiscrepancyTenantRow[]; count: number };
    flagOffSubOnTenants: { items: DiscrepancyTenantRow[]; count: number };
  } | null;
  loading: boolean;
  error: Error | null;
}) {
  // Server-supplied — guaranteed non-null whenever this component
  // renders because the parent only mounts us when `flag.subscriptionLabel`
  // is non-null. The `?? "subscription"` is defensive for the
  // hypothetical loading-race case where the row was filled from a
  // stale cache without the new field.
  const subscriptionLabel = flag.subscriptionLabel ?? "subscription";
  if (loading && !overview) {
    return (
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Subscription discrepancies
        </Typography>
        <CircularProgress size={16} />
      </Stack>
    );
  }
  if (error) {
    return (
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Subscription discrepancies
        </Typography>
        <Alert severity="error">Failed to load subscription overview: {error.message}</Alert>
      </Stack>
    );
  }
  if (!overview) return null;

  const totalCount = overview.flagOnSubOffCount + overview.flagOffSubOnCount;
  if (totalCount === 0) return null;

  const rows: UnifiedDiscrepancyRow[] = [
    ...overview.flagOnSubOffTenants.items.map<UnifiedDiscrepancyRow>((t) => ({
      ...t,
      direction: "flagOnSubOff",
    })),
    ...overview.flagOffSubOnTenants.items.map<UnifiedDiscrepancyRow>((t) => ({
      ...t,
      direction: "flagOffSubOn",
    })),
  ].sort((a, b) => a.tenantName.localeCompare(b.tenantName));

  // `loadedItems` is what we actually rendered into the merged list;
  // `loadedTotal` is what the server says exists across both buckets.
  // They can differ if either bucket was truncated server-side
  // (current page size is 50). Surface a hint when that happens so
  // staff know to drill in for the long tail.
  const loadedItems = rows.length;
  const hasMore = loadedItems < totalCount;

  const flagOnSubOffTooltip = `Flag ON but ${subscriptionLabel} subscription is FORMER_CUSTOMER or unset.`;
  const flagOffSubOnTooltip = `Paying for ${subscriptionLabel} but the flag is currently OFF.`;

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Subscription discrepancies
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({hasMore ? `${loadedItems} of ${totalCount}` : totalCount})
        </Typography>
      </Stack>
      <Stack
        spacing={0}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          maxHeight: 280,
          overflowY: "auto",
        }}
      >
        {rows.map((tenant, idx) => {
          const isFlagOnSubOff = tenant.direction === "flagOnSubOff";
          return (
            <Stack
              key={`${tenant.direction}:${tenant.tenantId}`}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.5,
                py: 1,
                borderTop: idx === 0 ? 0 : 1,
                borderColor: "divider",
              }}
            >
              <MuiLink
                component={RouterLink}
                to={`/staff/implementation/${tenant.tenantId}?tab=feature-flags`}
                underline="hover"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Typography variant="body2" noWrap>
                  {tenant.tenantName}
                </Typography>
              </MuiLink>
              {tenant.isPartner && <Chip size="small" label="Partner" />}
              {/* "Former" badge only on the flag-on/sub-off side: on
                  that side `FORMER_CUSTOMER` (was paying, churned) is
                  the actionable distinction from `null` (never paid —
                  staff override / fraud / config gap). On the sub-on
                  side the status is always `CUSTOMER` by definition,
                  so the badge would be noise. */}
              {isFlagOnSubOff &&
                tenant.subscriptionStatus === ModuleSubscriptionStatus.FormerCustomer && (
                  <Tooltip title="Subscription is FORMER_CUSTOMER — was a paying customer, now lapsed">
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Former"
                      aria-label="Former customer"
                    />
                  </Tooltip>
                )}
              {/* Two visually distinct chip colors so staff can scan
                  the column without reading labels: warning (amber)
                  for "Flag ON / Sub OFF" (lapsed-customer-with-
                  access, the riskier case), info (blue) for "Sub ON
                  / Flag OFF" (paying-but-can't-use, the customer-
                  impact case). */}
              <Tooltip title={isFlagOnSubOff ? flagOnSubOffTooltip : flagOffSubOnTooltip}>
                <Chip
                  size="small"
                  color={isFlagOnSubOff ? "warning" : "info"}
                  label={isFlagOnSubOff ? "Flag ON / Sub OFF" : "Sub ON / Flag OFF"}
                />
              </Tooltip>
            </Stack>
          );
        })}
      </Stack>
      {hasMore && (
        <Typography variant="caption" color="text.secondary">
          Showing the first {loadedItems}. Open the Subscriptions staff page to drill in further.
        </Typography>
      )}
    </Stack>
  );
}

function TenantsOnList({ flag }: { flag: FlagRow }) {
  const [searchInput, setSearchInput] = useState("");
  // Debounce so the search box doesn't fire a network request on every
  // keystroke. 300ms feels responsive without being chatty for the
  // longest-tail flags (TierIIModule etc.) where the result set is
  // hundreds of tenants.
  const [debouncedSearch] = useDebounce(searchInput.trim(), 300);
  const [pageSize, setPageSize] = useState(TENANTS_LIST_INITIAL_PAGE_SIZE);

  // Compose the omnisearch query. Free-text terms after `hasFlag:KEY`
  // fall through to the resolver's default columns (`name`, `id`), so a
  // user typing `acme` sees only tenants with this flag whose name or id
  // matches "acme".
  const search = debouncedSearch ? `hasFlag:${flag.key} ${debouncedSearch}` : `hasFlag:${flag.key}`;

  const { data, loading, error } = useQuery(FLAG_TENANTS_QUERY, {
    variables: { search, pageSize },
    fetchPolicy: "cache-and-network",
  });

  const items = data?.tenants.items ?? [];
  const totalCount = data?.tenants.count;
  const hasMore = totalCount !== undefined && items.length < totalCount;
  const isFiltered = debouncedSearch.length > 0;

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Tenants with this flag enabled
        </Typography>
        {totalCount !== undefined && (
          <Typography variant="caption" color="text.secondary">
            (
            {isFiltered
              ? `${items.length} of ${totalCount} matching`
              : hasMore
                ? `${items.length} of ${totalCount}`
                : totalCount}
            )
          </Typography>
        )}
      </Stack>
      {flag.defaultValue && (
        <Typography variant="caption" color="text.secondary">
          Default-on flag: only tenants with explicit overrides are listed.
        </Typography>
      )}
      <TextField
        size="small"
        placeholder="Search tenants by name…"
        value={searchInput}
        onChange={(e) => {
          // Reset pagination when the search term changes — otherwise a
          // user who clicked "Load more" then narrowed their search
          // would silently keep fetching a 200/300 page when 100 will do.
          setSearchInput(e.target.value);
          setPageSize(TENANTS_LIST_INITIAL_PAGE_SIZE);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      {loading && !data && <CircularProgress size={16} />}
      {error && (
        <Alert severity="error" sx={{ mt: 0.5 }}>
          Failed to load tenants: {error.message}
        </Alert>
      )}
      {data && items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {isFiltered
            ? "No tenants match that search."
            : "No tenants currently have this flag enabled."}
        </Typography>
      )}
      {data && items.length > 0 && (
        <Stack
          spacing={0}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {items.map((tenant, idx) => (
            <Stack
              key={tenant.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.5,
                py: 1,
                borderTop: idx === 0 ? 0 : 1,
                borderColor: "divider",
              }}
            >
              <MuiLink
                component={RouterLink}
                to={`/staff/implementation/${tenant.id}?tab=feature-flags`}
                underline="hover"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Typography variant="body2" noWrap>
                  {tenant.name}
                </Typography>
              </MuiLink>
              {tenant.isDemo && <Chip size="small" label="Demo" />}
              {tenant.isDisabled && <Chip size="small" label="Disabled" color="error" />}
            </Stack>
          ))}
        </Stack>
      )}
      {hasMore && (
        <Button
          size="small"
          variant="text"
          disabled={loading}
          onClick={() => setPageSize((s) => s + TENANTS_LIST_LOAD_MORE_INCREMENT)}
          sx={{ alignSelf: "flex-start" }}
        >
          {loading ? "Loading…" : `Load ${TENANTS_LIST_LOAD_MORE_INCREMENT} more`}
        </Button>
      )}
    </Stack>
  );
}

/**
 * Right-side drawer scoped to a single feature flag. Read-only
 * inspector + inline annotation editor.
 *
 * The `flagsByKey` map is supplied so the dependencies section can
 * render rich chips that drill from one flag's drawer to another
 * without re-querying.
 */
export function FlagDetailDrawer({
  flag,
  flagsByKey,
  customerTenantCount,
  onSelectFlag,
  onClose,
}: {
  flag: FlagRow | null;
  flagsByKey: Map<string, FlagRow>;
  customerTenantCount: number;
  onSelectFlag: (flag: FlagRow) => void;
  onClose: () => void;
}) {
  const alerts = useAlerts();
  const copyToClipboard = useCopyToClipboard();
  const [editing, setEditing] = useState(false);

  // Subscription overlap is only meaningful for flags the server has
  // mapped to a SFDC subscription column. We use the server-supplied
  // `subscriptionLabel` (null = no mapping) as the single signal so
  // the frontend stays out of the column-name mapping business. Skip
  // the network call entirely for non-mappable flags, and only fire
  // when the drawer is actually open (`flag !== null`).
  const flagKey: FeatureFlagKey | null = flag?.key ?? null;
  const hasSubscriptionMapping = flag !== null && flag.subscriptionLabel !== null;
  const {
    data: overviewData,
    loading: overviewLoading,
    error: overviewError,
  } = useQuery(FLAG_SUBSCRIPTION_OVERVIEW_QUERY, {
    skip: !flag || !hasSubscriptionMapping,
    variables: { flagKey: flagKey ?? "", pageSize: SUBSCRIPTION_DISCREPANCY_PAGE_SIZE },
    fetchPolicy: "cache-and-network",
  });
  const overview = overviewData?.flagSubscriptionOverview ?? null;

  const onCopy = async () => {
    if (!flag) return;
    await copyToClipboard(flag.key);
    alerts.success(`Copied "${flag.key}"`);
  };

  // Reset the editor when switching between flags so a half-open editor
  // for flag A doesn't leak into flag B's drawer state.
  const headerKey = flag?.key ?? null;
  const childFlagKeys = flag
    ? Array.from(flagsByKey.values())
        .filter((f) => f.parentKey === flag.key)
        .map((f) => f.key)
    : [];

  return (
    <Drawer
      anchor="right"
      open={flag !== null}
      onClose={() => {
        setEditing(false);
        onClose();
      }}
      slotProps={{ paper: { sx: { scrollbarGutter: "stable" } } }}
    >
      <Box sx={{ width: { xs: "100vw", md: 560 }, p: 3, bgcolor: "background.paper" }}>
        {flag && (
          // `spacing={4}` provides the visual section break that the
          // previous <Divider />s gave us, but without the heavy chrome.
          <Stack spacing={4} key={headerKey}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h5">{flag.label}</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Key:
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {flag.key}
                    </Typography>
                    {/* Copy is intentionally key-only — the label "Key:"
                        is decorative; staff almost always want to paste
                        the bare key into code or queries. */}
                    <Tooltip title="Copy key">
                      <IconButton size="small" onClick={onCopy} aria-label="Copy key">
                        <ContentCopy fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Stack>

              <PropertyStrip flag={flag} />

              <Typography variant="body2">{flag.description}</Typography>
            </Stack>

            {/* Suppress the entire dependency section (and its
                surrounding spacing) when there are no parent / requires
                / add-on relationships to show — otherwise the empty
                wrapper produces double spacing between the description
                and "Tenant Adoption". */}
            {(flag.parentKey || flag.requires.length > 0 || childFlagKeys.length > 0) && (
              <Stack spacing={2}>
                <DependencySection
                  title="Parent module"
                  keys={flag.parentKey ? [flag.parentKey] : []}
                  flagsByKey={flagsByKey}
                  onSelect={onSelectFlag}
                />
                <DependencySection
                  title="Requires"
                  keys={[...flag.requires]}
                  flagsByKey={flagsByKey}
                  onSelect={onSelectFlag}
                />
                <DependencySection
                  title="Add-ons"
                  keys={childFlagKeys}
                  flagsByKey={flagsByKey}
                  onSelect={onSelectFlag}
                />
              </Stack>
            )}

            <AdoptionStats
              flag={flag}
              customerTenantCount={customerTenantCount}
              subscribedCount={overview?.subscribedCount}
              subscriptionLabel={flag.subscriptionLabel ?? undefined}
            />

            {/* Annotation renders above both the discrepancies and
                tenants lists: the lists scroll inside their own
                panes and can grow long (100+ tenants for popular
                flags), so keeping notes/tags higher up means staff
                don't have to scroll past either list to add a
                comment. */}
            <Stack spacing={1}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                <Typography variant="overline" color="text.secondary">
                  Annotation
                </Typography>
                {!editing && (
                  <Button size="small" onClick={() => setEditing(true)}>
                    Edit notes &amp; tags
                  </Button>
                )}
              </Stack>
              {editing ? (
                <FlagAnnotationEditor
                  flag={flag}
                  onCancel={() => setEditing(false)}
                  onSaved={() => setEditing(false)}
                />
              ) : (
                // `spacing={1.5}` separates the three rows (notes,
                // tags, last-edited) enough that the tag chips feel
                // distinct from the notes paragraph above them; tighter
                // values made the chips read like a tail of the
                // sentence.
                <Stack spacing={1.5}>
                  {flag.notes ? (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {flag.notes}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No notes yet.
                    </Typography>
                  )}
                  {flag.tags.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {flag.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                    </Stack>
                  )}
                  {flag.annotationUpdatedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Last edited {new Date(flag.annotationUpdatedAt).toLocaleString()}
                      {flag.annotationUpdatedBy ? ` by ${flag.annotationUpdatedBy}` : ""}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>

            {hasSubscriptionMapping && (
              <SubscriptionDiscrepancies
                flag={flag}
                overview={overview}
                loading={overviewLoading}
                error={overviewError ?? null}
              />
            )}

            <TenantsOnList flag={flag} />
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
